import { useMemo } from "react";
import { usePutData } from "@/hooks/services/use-query-hooks";
import { UpdateJobOrderRanks } from "@/hooks/services/pps/handlers/job-orders-dynamic-api";
import { toast } from "sonner";

interface UseJobOrderRankUpdateOptions {
  workspaceId: number;
  onSuccess?: () => void;
  onError?: (error: any) => void;
}

interface JobOrderData {
  id: number;
  rank?: string;
  [key: string]: any;
}

/**
 * Custom hook to handle drag-and-drop row reordering with lexorank API
 *
 * @param workspaceId - The workspace ID for the job orders
 * @param onSuccess - Callback function to execute on successful rank update
 * @param onError - Callback function to execute on error
 *
 * @returns handleReorder function and loading state
 *
 * @example
 * const { handleReorder, isLoading } = useJobOrderRankUpdate({
 *   workspaceId: 1,
 *   onSuccess: () => refetch(),
 * });
 *
 * // In DataTable onRowOrderChange callback:
 * await handleReorder(tableData, oldIndex, newIndex);
 */
export function useJobOrderRankUpdate({
  workspaceId,
  onSuccess,
  onError,
}: UseJobOrderRankUpdateOptions) {
  const updateRanks = usePutData(UpdateJobOrderRanks());

  const handleReorder = useMemo(
    () =>
      async (
        data: JobOrderData[],
        oldIndex: number,
        newIndex: number,
        selectedIds?: number[]
      ): Promise<void> => {
        // Validate data
        if (!data || data.length === 0) {
          console.warn("No data available for reordering");
          return;
        }

        // No change needed
        if (oldIndex === newIndex) {
          console.log("No reorder needed (same position)");
          return;
        }

        // Validate workspace
        if (!workspaceId || workspaceId <= 0) {
          toast.error("Invalid workspace");
          return;
        }

        // Determine which IDs to move
        let jobOrderIds: number[];

        if (selectedIds && selectedIds.length > 0) {
          // Batch update: move all selected rows
          jobOrderIds = selectedIds;
        } else {
          // Single update: move only the dragged row
          const draggedJobOrder = data[oldIndex];
          if (!draggedJobOrder?.id) {
            console.error("Invalid job order at index", oldIndex);
            toast.error("Invalid job order selected");
            return;
          }
          jobOrderIds = [draggedJobOrder.id];
        }

        // Determine reference position based on drag direction
        let afterJobOrderId: number | null = null;
        let beforeJobOrderId: number | null = null;

        if (newIndex === 0) {
          // Move to top - both null (lexorank will place at beginning)
          afterJobOrderId = null;
          beforeJobOrderId = null;
        } else if (newIndex === data.length - 1) {
          // Move to bottom - after last item
          const lastItem = data[data.length - 1];
          afterJobOrderId = lastItem.id;
          beforeJobOrderId = null;
        } else if (oldIndex < newIndex) {
          // Moving down - place after the item at newIndex
          // When dragging down, the visual position shifts, so we need to place after the target
          const afterItem = data[newIndex];
          afterJobOrderId = afterItem.id;
          beforeJobOrderId = null;
        } else {
          // Moving up - place before the item at newIndex
          // When dragging up, we want to insert before the target item
          const beforeItem = data[newIndex];
          afterJobOrderId = null;
          beforeJobOrderId = beforeItem.id;
        }

        // Build API payload
        const payload = {
          workspaceId,
          updates: {
            jobOrderIds,
            afterJobOrderId,
            beforeJobOrderId,
          },
        };

        try {
          await new Promise<void>((resolve, reject) => {
            updateRanks.mutate(payload, {
              onSuccess: () => {
                const message =
                  jobOrderIds.length === 1
                    ? "Job order position updated successfully"
                    : `${jobOrderIds.length} job orders repositioned successfully`;
                toast.success(message);
                onSuccess?.();
                resolve();
              },
              onError: (error: any) => {
                console.error("Failed to update job order rank:", error);

                // Extract error message from response
                const errorMessage =
                  error?.response?.data?.message?.[0] ||
                  error?.message ||
                  "Failed to update job order position";

                toast.error(errorMessage);
                onError?.(error);
                reject(error);
              },
            });
          });
        } catch (error) {
          // Error already handled in onError callback
        }
      },
    [workspaceId, updateRanks, onSuccess, onError]
  );

  return {
    handleReorder,
    isLoading: updateRanks.isPending,
  };
}
